import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  Timestamp,
  getDoc,
  serverTimestamp,
  increment,
  writeBatch,
  DocumentReference,
  FieldValue,
  runTransaction,
  deleteDoc,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Booking, BookingStatus, PaymentStatus, Event } from '../types/models';
import { toast } from 'sonner';
import { BookingError, BOOKING_ERROR_CODES } from '../utils/errors';

declare module '../types/models' {
  interface Booking {
    [key: string]: any;
  }
}

export interface AttendeeInfo {
  name: string;
  email: string;
  phone?: string;
}

export interface BookingData {
  eventId: string;
  userId: string;
  ticketTierId: string;
  ticketTierName: string;
  quantity: number;
  totalPrice: number;
  status: BookingStatus;
  paymentStatus: PaymentStatus;
  attendeeInfo: AttendeeInfo[];
  createdAt: Date;
  updatedAt: Date;
  paymentDetails?: {
    method: string;
    transactionId: string;
    amount: number;
    currency: string;
    paidAt: Date;
  };
  [key: string]: any; // Allow dynamic key access
}

class BookingService {
  private readonly bookingsCollection = collection(db, 'bookings');
  private readonly eventsCollection = collection(db, 'events');

  private validateBookingData(data: BookingData): void {
    if (!data.eventId) throw new Error('Event ID is required');
    if (!data.userId) throw new Error('User ID is required');
    if (!data.ticketTierId) throw new Error('Ticket tier ID is required');
    if (!data.ticketTierName) throw new Error('Ticket tier name is required');
    if (!data.quantity || data.quantity < 1) throw new Error('Invalid quantity');
    if (data.totalPrice == null || isNaN(data.totalPrice) || data.totalPrice < 0) throw new Error('Invalid total price');
    if (!data.status) throw new Error('Booking status is required');
    if (!data.paymentStatus) throw new Error('Payment status is required');
    if (!Array.isArray(data.attendeeInfo) || data.attendeeInfo.length === 0) {
      throw new Error('Attendee information is required');
    }

    // Validate each attendee
    data.attendeeInfo.forEach((attendee, index) => {
      if (!attendee.name) throw new Error(`Name is required for attendee ${index + 1}`);
      if (!attendee.email) throw new Error(`Email is required for attendee ${index + 1}`);
      if (!attendee.email.includes('@')) throw new Error(`Invalid email for attendee ${index + 1}`);
    });

    // Validate payment details if provided
    if (data.paymentDetails) {
      const { method, transactionId, amount, currency, paidAt } = data.paymentDetails;
      if (!method) throw new Error('Payment method is required');
      if (!transactionId) throw new Error('Transaction ID is required');
      if (amount == null || isNaN(amount) || amount < 0) throw new Error('Invalid payment amount');
      if (!currency) throw new Error('Currency is required');
      if (!paidAt) throw new Error('Payment date is required');
    }
  }

  private sanitizeBookingData(data: BookingData) {
    // Convert dates to Firestore Timestamps
    const sanitized = {
      ...data,
      createdAt: Timestamp.fromDate(data.createdAt),
      updatedAt: Timestamp.fromDate(data.updatedAt),
      paymentDetails: data.paymentDetails ? {
        ...data.paymentDetails,
        paidAt: Timestamp.fromDate(data.paymentDetails.paidAt)
      } : null, // Set to null if undefined to avoid Firestore errors
      // Clean up attendee info
      attendeeInfo: data.attendeeInfo.map(attendee => ({
        name: attendee.name.trim(),
        email: attendee.email.trim().toLowerCase(),
        phone: attendee.phone?.trim() || null // Set to null if undefined
      }))
    };

    // Remove any undefined values to prevent Firestore errors
    Object.keys(sanitized).forEach(key => {
      if ((sanitized as Record<string, any>)[key] === undefined) {
        delete (sanitized as Record<string, any>)[key];
      }
    });

    return sanitized;
  }

  /**
   * Creates a new booking and updates event ticket availability
   */
  async createBooking(bookingData: BookingData): Promise<Booking> {
    try {
      // Validate the booking data
      this.validateBookingData(bookingData);

      // Sanitize the data for Firestore
      const sanitizedData = this.sanitizeBookingData(bookingData);

      // Create the booking with a transaction to ensure ticket availability
      const bookingRef = await runTransaction(db, async (transaction) => {
        // Check ticket availability
        const eventRef = doc(this.eventsCollection, bookingData.eventId);
        const eventDoc = await transaction.get(eventRef);

        if (!eventDoc.exists()) {
          throw new Error('Event not found');
        }

        const eventData = eventDoc.data() as Event;
        const ticketTier = eventData.ticketTiers?.find(
          (tier: any) => tier.id === bookingData.ticketTierId
        );

        if (!ticketTier) {
          throw new Error('Ticket tier not found');
        }

        const availableTickets = ticketTier.quantity - (ticketTier.sold || 0);
        if (availableTickets < bookingData.quantity) {
          throw new Error('Not enough tickets available');
        }

        // Generate a new unique document reference for each booking
        const bookingRef = doc(collection(db, 'bookings'));
        
        transaction.set(bookingRef, sanitizedData);

        // Update ticket availability
        transaction.update(eventRef, {
          ticketTiers: eventData.ticketTiers.map((tier: any) =>
            tier.id === bookingData.ticketTierId
              ? { ...tier, sold: (tier.sold || 0) + bookingData.quantity }
              : tier
          )
        });

        return bookingRef;
      });

      // Get the created booking
      const bookingSnap = await getDoc(bookingRef);
      if (!bookingSnap.id) {
        throw new Error('Booking creation failed: missing booking ID');
      }
      return { id: bookingSnap.id, ...bookingSnap.data() } as Booking;
    } catch (error: any) {
      console.error('Error creating booking:', error);
      
      // Check if the error is related to missing index
      if (error.message && error.message.includes('requires an index')) {
        toast.error(
          'System is being optimized for first-time use. Please try again in a few minutes.',
          { duration: 5000 }
        );
        throw new BookingError(
          'System is being optimized. Please try again shortly.',
          BOOKING_ERROR_CODES.SYSTEM_OPTIMIZATION,
          true
        );
      }
      
      throw error;
    }
  }
  
  /**
   * Gets all bookings for a specific user
   */
  async getUserBookings(userId: string): Promise<Booking[]> {
    try {
      const q = query(
        this.bookingsCollection,
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Booking[];
    } catch (error) {
      console.error('Error fetching user bookings:', error);
      throw error;
    }
  }
  
  /**
   * Gets all bookings for a specific event
   */
  async getEventBookings(eventId: string): Promise<Booking[]> {
    try {
      const q = query(
        this.bookingsCollection,
        where('eventId', '==', eventId),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Booking[];
    } catch (error) {
      console.error('Error fetching event bookings:', error);
      throw error;
    }
  }
  
  /**
   * Gets a specific booking by ID
   */
  async getBookingById(bookingId: string): Promise<Booking | null> {
    try {
      const docRef = doc(this.bookingsCollection, bookingId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        return null;
      }
      
      return {
        id: docSnap.id,
        ...docSnap.data()
      } as Booking;
    } catch (error) {
      console.error('Error fetching booking:', error);
      throw error;
    }
  }
  
  /**
   * Gets all bookings for a specific user and event
   */
  async getUserEventBookings(userId: string, eventId: string): Promise<Booking[]> {
    try {
      const q = query(
        this.bookingsCollection,
        where('userId', '==', userId),
        where('eventId', '==', eventId),
        where('status', '!=', 'cancelled'),
        orderBy('status'),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Booking[];
    } catch (error: any) {
      console.error('Error fetching user event bookings:', error);
      
      // Check if the error is related to missing index
      if (error.message && error.message.includes('requires an index')) {
        toast.error(
          'System is being optimized for first-time use. Please try again in a few minutes.',
          { duration: 5000 }
        );
        return []; // Return empty array instead of throwing
      }
      
      throw error;
    }
  }
  
  /**
   * Updates a booking's status and adjusts event ticket availability if cancelled
   */
  async updateBookingStatus(bookingId: string, status: BookingStatus): Promise<void> {
    try {
      await runTransaction(db, async (transaction) => {
        const bookingRef = doc(this.bookingsCollection, bookingId);
        const bookingDoc = await transaction.get(bookingRef);
        if (!bookingDoc.exists()) {
          throw new BookingError(
            'Booking not found',
            BOOKING_ERROR_CODES.BOOKING_NOT_FOUND,
            false
          );
        }
        const booking = bookingDoc.data() as Booking;
        // Only update if status actually changed
        if (booking.status !== status) {
          // Handle ticket availability changes for cancelled bookings
          if (status === 'cancelled' && booking.status !== 'cancelled') {
            const eventRef = doc(this.eventsCollection, booking.eventId);
            const eventDoc = await transaction.get(eventRef);
            if (eventDoc.exists()) {
              const eventData = eventDoc.data();
              const ticketTiers = Array.isArray(eventData.ticketTiers) ? eventData.ticketTiers : [];
              const ticketTierIndex = ticketTiers.findIndex(tier => tier.id === booking.ticketTierId);
              if (ticketTierIndex !== -1) {
                // Update the specific ticket tier's sold count
                transaction.update(eventRef, {
                  [`ticketTiers.${ticketTierIndex}.sold`]: increment(-booking.quantity)
                });
              }
            }
          }
          // Update booking status
          transaction.update(bookingRef, {
            status,
            updatedAt: serverTimestamp()
          });
        }
      });
    } catch (error) {
      console.error('Error updating booking status:', error);
      throw error;
    }
  }
  
  /**
   * Updates payment status of a booking
   */
  async updatePaymentStatus(bookingId: string, paymentStatus: PaymentStatus): Promise<void> {
    try {
      const bookingRef = doc(this.bookingsCollection, bookingId);
      const bookingDoc = await getDoc(bookingRef);
      if (bookingDoc.exists()) {
        const currentStatus = bookingDoc.data().paymentStatus;
        // Only update if payment status actually changed
        if (currentStatus !== paymentStatus) {
          await updateDoc(bookingRef, {
            paymentStatus,
            updatedAt: serverTimestamp()
          });
        }
      }
    } catch (error) {
      console.error('Error updating payment status:', error);
      throw error;
    }
  }
  
  /**
   * Updates attendee information for a booking
   */
  async updateAttendeeInfo(
    bookingId: string, 
    attendeeInfo: Array<{name: string; email: string; phone?: string}>
  ): Promise<void> {
    try {
      const bookingRef = doc(this.bookingsCollection, bookingId);
      const booking = await this.getBookingById(bookingId);
      if (!booking) {
        throw new Error('Booking not found');
      }
      if (attendeeInfo.length > booking.quantity) {
        throw new Error('Number of attendees exceeds booking quantity');
      }
      // Only update if attendee info actually changed
      if (JSON.stringify(booking.attendeeInfo) !== JSON.stringify(attendeeInfo)) {
        await updateDoc(bookingRef, {
          attendeeInfo,
          updatedAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('Error updating attendee info:', error);
      throw error;
    }
  }

  /**
   * Helper function to normalize timestamps
   */
  private normalizeTimestamp(timestamp: any): Timestamp {
    if (timestamp instanceof Timestamp) {
      return timestamp;
    }
    
    if (timestamp instanceof Date) {
      return Timestamp.fromDate(timestamp);
    }
    
    if (timestamp && typeof timestamp === 'object' && 'seconds' in timestamp) {
      const tsObject = timestamp as { seconds: number; nanoseconds?: number };
      return new Timestamp(
        tsObject.seconds,
        tsObject.nanoseconds || 0
      );
    }
    
    console.warn('Could not determine timestamp format, using current timestamp');
    return Timestamp.now();
  }

  async cancelBooking(bookingId: string) {
    try {
      await runTransaction(db, async (transaction) => {
        const bookingRef = doc(this.bookingsCollection, bookingId);
        const bookingSnap = await transaction.get(bookingRef);

        if (!bookingSnap.exists()) {
          throw new Error('Booking not found');
        }

        const bookingData = bookingSnap.data();
        
        // Update the event's ticket availability
        const eventRef = doc(this.eventsCollection, bookingData.eventId);
        const eventSnap = await transaction.get(eventRef);

        if (!eventSnap.exists()) {
          throw new Error('Event not found');
        }

        const eventData = eventSnap.data();
        transaction.update(eventRef, {
          ticketTiers: eventData.ticketTiers.map((tier: any) =>
            tier.id === bookingData.ticketTierId
              ? { ...tier, sold: Math.max(0, (tier.sold || 0) - bookingData.quantity) }
              : tier
          )
        });

        // Update booking status
        transaction.update(bookingRef, {
          status: 'cancelled' as BookingStatus,
          updatedAt: serverTimestamp()
        });
      });
    } catch (error) {
      console.error('Error cancelling booking:', error);
      throw error;
    }
  }
}

// Create and export a singleton instance
export const bookingService = new BookingService();

// Helper functions for easy importing
export const createBooking = async (bookingData: BookingData): Promise<Booking> => {
  return bookingService.createBooking(bookingData);
};

export const getUserBookings = async (userId: string): Promise<Booking[]> => {
  return bookingService.getUserBookings(userId);
};

export const getEventBookings = async (eventId: string): Promise<Booking[]> => {
  return bookingService.getEventBookings(eventId);
};

export const getBookingById = async (bookingId: string): Promise<Booking | null> => {
  return bookingService.getBookingById(bookingId);
};

export const updateBookingStatus = async (bookingId: string, status: BookingStatus): Promise<void> => {
  return bookingService.updateBookingStatus(bookingId, status);
};

export const updatePaymentStatus = async (bookingId: string, paymentStatus: PaymentStatus): Promise<void> => {
  return bookingService.updatePaymentStatus(bookingId, paymentStatus);
};

export const updateAttendeeInfo = async (
  bookingId: string, 
  attendeeInfo: Array<{name: string; email: string; phone?: string}>
): Promise<void> => {
  return bookingService.updateAttendeeInfo(bookingId, attendeeInfo);
};

export const cancelBooking = async (bookingId: string): Promise<void> => {
  return bookingService.cancelBooking(bookingId);
}; 